import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { RoutingService } from '@shared/services/routing.service';
import { UserService } from '@shared/services/user.service';
import { Subject } from 'rxjs';
import { LayoutService } from '@shared/services/layout.service';
import { ContactTracingService } from './contact-tracing/services/contact-tracing.service';
import { LcoConfig } from '@shared/config';
import { TranslateService } from '@ngx-translate/core';
import { translates } from '@shared/translation';
import { ContactTracingBaseRoute } from './contact-tracing/contact-tracing-routes-map';
import { routerTransition, slideInAnimation } from '@shared/animations';
import { QrCodeService } from '@shared/services/qr-code.service';
import { QR_CODE_TYPE } from '@shared/models';
import { LcoService } from './lco/services/lco.service';
import { LcoRoutesMap } from './lco/lco-routing.module';
import { WebsocketService } from '@shared/services/websocket.service';
import { WINDOW } from '@shared/services/window.service';
import { Location } from '@angular/common';
import { routes } from './app-routing.module';
// import { SwUpdate } from '@angular/service-worker';
import { LoggerService } from '@shared/services/logger.service';
// import { UpdateService } from '@shared/services/update.service';
import { HelperService } from '@shared/helpers/helper.service';

import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import { registerLocaleData } from '@angular/common';

registerLocaleData(localeFr, 'fr-CA', localeFrExtra);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    slideInAnimation,
    routerTransition
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding('class.main-bg') mainBg: boolean = false;
  @HostBinding('class.dark-bg') darkBg: boolean = false;

  private destroySubject: Subject<any> = new Subject<any>();
  config: LcoConfig;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private routingService: RoutingService,
              public userService: UserService,
              public layoutService: LayoutService,
              private contactTracingService: ContactTracingService,
              public translate: TranslateService,
              private renderer: Renderer2,
              @Inject(WINDOW) private window: Window,
              private qrCodeService: QrCodeService,
              public lcoService: LcoService,
              private websocketService: WebsocketService,
              private location: Location,
              private cdr: ChangeDetectorRef,
              // public updates: SwUpdate,
              private logger: LoggerService,
              // private updateService: UpdateService,
              private helper: HelperService) {
    this.proceedGuidParam();

    this.layoutService.config
      .pipe(takeUntil(this.destroySubject))
      .subscribe((config: any) => {
        this.config = config;
        this.cdr.markForCheck();
      });

    this.handleTranslates();
  }

  ngOnInit() {
    this.handleRouting();

    if (this.userService.isAuthorized && this.lcoService.checkedIn) {
      this.userService.getCredentials(QR_CODE_TYPE.LOYALTY_MEMBER)
        .pipe(
          filter(x => !!x),
          takeUntil(this.destroySubject)
        )
        .subscribe((res: any) => {
          if (!this.websocketService.rxStomp?.connected()) {
            this.websocketService.setup();
          }
        });
    }
  }

  proceedGuidParam(): void {
    let url = '';

    if (this.window && this.window.location) {
      url = this.window.location.href;
    } else {
      url = this.location.path(true);
    }

    // this.logger.log(`proceedGuidParam this.location: `, this.location.path(true));
    // this.logger.log(`proceedGuidParam this.window: `, this.window);
    // this.logger.log(`proceedGuidParam url: `, url);

    if (url.includes('guid')) {
      this.layoutService.guid = url.split('pay')[1];
      this.layoutService.parsedGuid = this.helper.parseGuid(this.layoutService.guid);
      this.router.navigate(['/pay'], {replaceUrl: true, queryParams: this.layoutService.parsedGuid});
    }
  }

  private handleRouting() {
    // routing by query params
    this.route.queryParamMap
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (params: any) => {
        if (params.has('qr')) {
          console.log(`handleRouting`);
          this.qrCodeService.qrCodeParam = params.get('qr');
          await this.navigateByQrCode('');
          this.cdr.markForCheck();
        }
      });

    this.router.events
      .pipe(
        filter(x => x instanceof NavigationEnd),
        take(1),
        takeUntil(this.destroySubject),
      )
      .subscribe(async (event: any) => {
        this.logger.log(`urlAfterRedirects: `, event.urlAfterRedirects);

        const isLcoLink: boolean = !!Object.values(LcoRoutesMap).find(lcoLink => {
          return lcoLink !== LcoRoutesMap.base && event.urlAfterRedirects.includes(lcoLink);
        })

        // if (
        //   event.urlAfterRedirects.includes('?qr=')
        //   || event.urlAfterRedirects.includes('promo')
        //   || event.urlAfterRedirects.includes('pay')
        //   || event.urlAfterRedirects.includes('auth')
        //   || event.urlAfterRedirects.includes('admin')
        // ) {
        //   return;
        // }

        if (this.contactTracingService.isContactTracing && event.urlAfterRedirects.includes(ContactTracingBaseRoute)) {
          await this.navigateByQrCode(event.urlAfterRedirects);
        } else if (this.lcoService.isLco && isLcoLink) {
          await this.navigateByQrCode(event.urlAfterRedirects);
        } else if (event.urlAfterRedirects.includes('shell')) {
          return;
        } else {
          return;
          // this.routingService.redirectToLco();
        }

        this.cdr.markForCheck();
      });
  }

  private isUrlValid(urlAfterRedirects: string) {
    const urls = routes.filter(route => {
      return route.path && urlAfterRedirects.includes(route.path.split('/')[0])
    });
  }

  private async navigateByQrCode(urlAfterRedirects: string) {
    this.logger.log(`navigateByQrCode`);

    const parsedQrCode = await this.qrCodeService.getDataByQrCode(this.qrCodeService.qrCodeParam);

    if (!parsedQrCode) {
      this.layoutService.snackBarError('Invalid QR code', 'OK');
      this.routingService.redirectToLco();
    }

    switch (parsedQrCode.type) {
      case QR_CODE_TYPE.TEAM_MEMBER:
      case QR_CODE_TYPE.LOYALTY_MEMBER:
        this.contactTracingService.resetQrCode = this.qrCodeService.qrCodeParam;
        await this.contactTracingService.tryToUpdateAllContactTracingData();
        this.contactTracingService.handleContactTracingRouting();
        break;
      case QR_CODE_TYPE.TABLE:
        if (urlAfterRedirects.includes(LcoRoutesMap.rateThisSong)) {
          return;
        }

        await this.lcoService.updateLcoData();
        this.lcoService.handleLcoRouting();
        break;
      default:
        return;
    }
  }

  private handleTranslates() {
    // translates
    this.translate.addLangs(translates.map(t => t.label));
    this.translate.setDefaultLang(this.layoutService.defaultLang);
    this.translate.use(this.layoutService.lang);

    this.layoutService.language$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(lang => {
        this.translate.use(lang);
      });
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.animation;
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  private updateApp() {
    this.window.document.location.reload();
  }
}
